import {
  FolderIcon,
  QuestionFillIcon,
  showToast,
  Switch,
  Text,
  Tooltip,
} from '@mercai/clever'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { CardInfo } from '@/components'
import { formatLongDate } from '@/helpers'
import { ROUTES_ADMIN } from '@/router'
import { QUERIES_ADMIN, useOrderCenterSaleOrderUpdate } from '@/services'

import type { CardOrderInformationProps } from './card-order-information.type'

import styles from './card-order-information.module.css'

export const CardOrderInformation = ({
  isLoading,
  kam,
  quotation,
  createdAt,
  invoiceFolio,
  disableNotifications,
  uuid,
}: CardOrderInformationProps) => {
  const { t } = useTranslation('componentCardsOrderCenterShow')

  const queryClient = useQueryClient()

  const {
    mutateAsync: mutateAsyncUpdateSaleOrder,
    isPending: isPendingQuotation,
  } = useOrderCenterSaleOrderUpdate(uuid)

  const handleDisableNotification = async () => {
    const response = await mutateAsyncUpdateSaleOrder({
      disableNotifications: !disableNotifications,
    })

    if (response.success) {
      showToast({
        variant: 'success',
        title: t('orderInformationDisableNotificationSuccess'),
      })

      queryClient.refetchQueries({
        queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, uuid],
      })
    } else {
      showToast({
        variant: 'error',
        title: t('orderInformationDisableNotificationError'),
      })
    }
  }

  return (
    <CardInfo
      isLoading={isLoading}
      icon={<FolderIcon />}
      title={t('orderInformationTitle')}
      actions={<></>}
      items={[
        {
          label: (
            <Tooltip
              text={t('orderInformationDisableNotificationDescription')}
              orientation="right"
              widthText="12rem"
            >
              {t('orderInformationDisableNotification')}
              <QuestionFillIcon className={styles['content-row-info']} />:
            </Tooltip>
          ),
          value: (
            <div className={styles['row-switch']}>
              <Switch
                checked={!disableNotifications}
                onChange={handleDisableNotification}
                disabled={isPendingQuotation}
              />
              <Text variant="text-base">
                {disableNotifications
                  ? t('orderInformationDisableNotificationDisabled')
                  : t('orderInformationDisableNotificationEnabled')}
              </Text>
            </div>
          ),
        },
        {
          label: t('orderInformationKam'),
          value: kam || '-',
        },
        {
          label: t('orderInformationQuotation'),
          value: quotation ? (
            <Link
              className={styles.link}
              target="_blank"
              to={`${ROUTES_ADMIN.BUSINESS.QUOTATION.SHOW.fullPath(quotation.uuid)}`}
            >
              {`#${quotation.reference}`}
            </Link>
          ) : (
            '-'
          ),
        },
        {
          label: t('orderInformationCreator'),
          value: quotation?.creatorUser || '-',
        },
        {
          label: t('orderInformationCreatedAt'),
          value: createdAt ? formatLongDate(createdAt) : '-',
        },
        {
          label: t('orderInformationInvoiceFolio'),
          value: invoiceFolio || '-',
        },
      ]}
    />
  )
}
